import API from "../../../services/API";
import Formatter from "../../../services/Formatter";

/**
 * Organizational Chart (people-based). Spec: PRs/org-chart-people/SPEC.md section 5.
 * Every call goes through API.call (JWT + X-Authorization added for free).
 * Every HR write answers with the chart's updated draft, which replaces state.orgChart.draft.
 */

/* ------------------------------- staff ------------------------------- */

export const fetchOrgCharts = () => (dispatch) =>
  API.call({ method: "get", url: "/org-chart" })
    .then((result) => dispatch({ type: "ORG_CHARTS_LOADED", charts: result.data.content.charts }))
    .catch((e) => {
      dispatch(Formatter.alert_error(e));
      dispatch({ type: "ORG_CHARTS_FAILED" });
    });

/* -------------------------------- HR --------------------------------- */

const base = "/org-chart/manage";

export const fetchManageList = () => (dispatch) =>
  API.call({ method: "get", url: base })
    .then((result) => dispatch({ type: "ORG_CHART_MANAGE_LOADED", charts: result.data.content.charts }))
    .catch((e) => dispatch(Formatter.alert_error(e)));

export const fetchDraft = (id) => (dispatch) => {
  dispatch({ type: "ORG_CHART_DRAFT_CLEAR" });
  return API.call({ method: "get", url: base + "/" + id })
    .then((result) => dispatch({ type: "ORG_CHART_DRAFT_LOADED", draft: result.data.content }))
    .catch((e) => {
      dispatch(Formatter.alert_error(e));
      dispatch({ type: "ORG_CHART_DRAFT_FAILED" });
    });
};

/**
 * Shared by every editor write: success -> toast + new draft; a 422 is returned to the caller
 * (the panel shows it next to the field), anything else raises the standard alert. Rethrows.
 */
const write = (config, quiet) => (dispatch) =>
  API.call(config)
    .then((result) => {
      if (!quiet) dispatch(Formatter.alert_success(result, 3000));
      if (result.data.content && result.data.content.chart) {
        dispatch({ type: "ORG_CHART_DRAFT_LOADED", draft: result.data.content });
      }
      return result.data.content;
    })
    .catch((e) => {
      if (!e || e.status !== 422) dispatch(Formatter.alert_error(e));
      throw e;
    });

export const createChart = (data) => write({ method: "post", url: base, data });
export const renameChart = (id, name) => write({ method: "post", url: base + "/" + id + "/update", data: { name } });
export const saveDetailRows = (id, rows) => write({ method: "post", url: base + "/" + id + "/details", data: { rows } });
export const addItems = (id, data) => write({ method: "post", url: base + "/" + id + "/items", data });
export const updateItem = (id, itemId, data) => write({ method: "post", url: base + "/" + id + "/items/" + itemId + "/update", data });
export const removeItem = (id, itemId, children) =>
  write({ method: "delete", url: base + "/" + id + "/items/" + itemId, params: { children } });
export const removeLevel = (id, level) => write({ method: "post", url: base + "/" + id + "/remove-level", data: { level } });
export const uploadPhoto = (id, itemId, formData) =>
  write({ method: "post", url: base + "/" + id + "/items/" + itemId + "/photo", headers: { "Content-Type": "multipart/form-data" }, data: formData });
export const addGroup = (id, name) => write({ method: "post", url: base + "/" + id + "/groups", data: { name } });
export const updateGroup = (id, groupId, data) => write({ method: "post", url: base + "/" + id + "/groups/" + groupId + "/update", data });
export const deleteGroup = (id, groupId) => write({ method: "delete", url: base + "/" + id + "/groups/" + groupId });
export const publishChart = (id) => write({ method: "post", url: base + "/" + id + "/publish" });
export const discardDraft = (id) => write({ method: "post", url: base + "/" + id + "/discard" });

export const deleteChart = (id) => (dispatch) =>
  API.call({ method: "delete", url: base + "/" + id })
    .then((result) => {
      dispatch(Formatter.alert_success(result, 3000));
      dispatch({ type: "ORG_CHART_MANAGE_REMOVED", id });
      return true;
    })
    .catch((e) => {
      dispatch(Formatter.alert_error(e));
      return false;
    });

export const reorderCharts = (ids) => (dispatch) =>
  API.call({ method: "post", url: base + "/reorder", data: { ids } })
    .then((result) => dispatch({ type: "ORG_CHART_MANAGE_LOADED", charts: result.data.content.charts }))
    .catch((e) => dispatch(Formatter.alert_error(e)));

/** Employee picker: resolves to [{value, label}]; failures resolve to []. Nothing dispatched. */
export const searchEmployees = (q) => () =>
  API.call({ method: "get", url: base + "/employees", params: { q } })
    .then((result) =>
      (result.data.content || []).map((u) => ({ value: u.id, label: u.name + (u.job_title ? " · " + u.job_title : "") }))
    )
    .catch(() => []);
